import React, { useState, useEffect } from "react";
import { StyleSheet, View, Platform, TouchableOpacity } from "react-native";
import { Image } from "expo-image";
import {
  MediaModel,
  ILocation,
  isAudioMedia,
  isVideoMedia,
  isPdfMedia,
  isDocumentMedia,
  ITagAssociationMeta,
  DateParser,
  IDate,
  IEntityContactMeta,
  IEntityGroupMeta,
  IEntityMemoryMeta,
  IEntityPlaceMeta,
  IEntityAnchorMeta,
  Contact,
  GroupModel,
} from "common247";
import { ThemedText } from "../ThemedText";
import { ThemedInput } from "../ThemedInput";
import { ThemedButton } from "../ThemedButton";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";
import { ThemedView } from "../ThemedView";
import { ThemedRating } from "../ThemedRating";
import { Ionicons } from "@expo/vector-icons";
import { GoogleMapsLocationSelector } from "../location/GoogleMapsLocationSelector";
import { useAutoSave } from "@/hooks/useAutoSave";
import { AutoSaveIndicator } from "@/components/ui/AutoSaveIndicator";
import { useActiveFieldTracker } from "@/hooks/useActiveField";
import { DateSelector } from "@/components/date/DateSelector";
import { TagListStrip } from "../tags/TagListStrip";
import { EntityLinkStrip } from "../ui/EntityLinkStrip";
import { useFirebase } from "../firebase/useFirebase";
import { useThemeColor } from "@/hooks/useThemeColor";
import { extractAnchorCoordinates, type IPopoverAnchor } from "@/utils/popover-positioning";

import { VideoPlayer } from "./VideoPlayer";
import { ThemedAudioPlayer } from "./ThemedAudioPlayer";
import { CollaborativeEditor } from "../editor/CollaborativeEditor";
import { extractMediaMetadata } from "@/services/media-metadata";
import { useFeatureFlag } from "@/services/remote-config-service";
import { ExtractMediaInfoModal } from "./ExtractMediaInfoModal";

export interface IMediaEditorProps {
  media: MediaModel;
  isSaving: boolean;
  editable: boolean;
  onSave: (
    title: string,
    location?: ILocation,
    description?: string,
    favouriteRating?: number,
    tags?: Record<string, ITagAssociationMeta>,
    date?: string,
    contacts?: Record<string, IEntityContactMeta>,
    groups?: Record<string, IEntityGroupMeta>,
    memories?: Record<string, IEntityMemoryMeta>,
    places?: Record<string, IEntityPlaceMeta>,
    anchors?: Record<string, IEntityAnchorMeta>
  ) => void;
  contacts?: Contact[];
  groups?: GroupModel[];
  onCancel: () => void;
  children?: React.ReactNode;
  rightActions?: React.ReactNode;
}

function MediaPreview({
  url,
  contentType,
  fileName,
  title,
  name,
  style,
  resizeMode = "contain",
}: {
  url: string;
  contentType?: string;
  fileName?: string;
  title?: string;
  name?: string;
  style: any;
  resizeMode?: "cover" | "contain";
}) {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [url]);

  const mediaName = title || name || fileName;
  const isAudio = isAudioMedia(contentType, mediaName, url);
  const isVideo = isVideoMedia(contentType, mediaName, url);
  const isPdf = isPdfMedia(contentType, mediaName, url);
  const isDoc = isDocumentMedia(contentType, mediaName, url);

  if (isAudio) {
    if (resizeMode === "contain") {
      return (
        <View style={[style, styles.mediaPlayerContainer]}>
          <Ionicons name="musical-notes" size={48} color="#4A90E2" style={{ marginBottom: 12 }} />
          <ThemedAudioPlayer
            url={url}
            title={mediaName}
            style={{ width: "100%", maxWidth: 400 }}
          />
        </View>
      );
    }
    return (
      <View style={[style, styles.mediaPlaceholder, { backgroundColor: "rgba(74, 144, 226, 0.1)" }]}>
        <Ionicons name="musical-notes" size={64} color="#4A90E2" />
        <ThemedText style={styles.mediaPlaceholderText}>Audio Clip</ThemedText>
      </View>
    );
  }

  if (isVideo) {
    if (!url || !url.trim()) {
      return (
        <View style={[style, styles.mediaPlaceholder, { backgroundColor: "rgba(234, 67, 53, 0.1)" }]}>
          <Ionicons name="videocam-outline" size={48} color="#EA4335" />
          <ThemedText style={styles.mediaPlaceholderText}>Video Unavailable</ThemedText>
        </View>
      );
    }
    return (
      <VideoPlayer
        url={url}
        contentType={contentType}
        fileName={fileName}
        style={style}
        resizeMode={resizeMode}
      />
    );
  }

  if (isPdf) {
    return (
      <View
        testID="pdf-media-preview"
        style={[
          style,
          styles.mediaPlaceholder,
          {
            backgroundColor: "rgba(220, 53, 69, 0.08)",
            borderWidth: 1,
            borderColor: "rgba(220, 53, 69, 0.2)",
          },
        ]}
      >
        <Ionicons name="document-text" size={48} color="#DC3545" />
        <ThemedText style={[styles.mediaPlaceholderText, { color: "#DC3545", fontWeight: "600", marginTop: 8 }]}>
          PDF Document
        </ThemedText>
      </View>
    );
  }


  if (isDoc) {
    return (
      <View
        testID="document-media-preview"
        style={[
          style,
          styles.mediaPlaceholder,
          {
            backgroundColor: "rgba(74, 144, 226, 0.08)",
            borderWidth: 1,
            borderColor: "rgba(74, 144, 226, 0.2)",
          },
        ]}
      >
        <Ionicons name="document-text-outline" size={48} color="#4A90E2" />
        <ThemedText style={[styles.mediaPlaceholderText, { color: "#4A90E2", fontWeight: "600", marginTop: 8 }]}>
          Document
        </ThemedText>
      </View>
    );
  }

  if (hasError || !url) {
    return (
      <View style={[style, styles.mediaPlaceholder, { backgroundColor: "rgba(150, 150, 150, 0.08)" }]}>
        <Ionicons name="image-outline" size={48} color="#999" />
        <ThemedText style={styles.mediaPlaceholderText}>Image unavailable</ThemedText>
      </View>
    );
  }

  const cleanUrl = url ? url.trim() : "";
  const sanitizedUrl = cleanUrl.includes(" ") ? encodeURI(cleanUrl) : cleanUrl;

  return (
    <Image
      key={sanitizedUrl}
      recyclingKey={sanitizedUrl}
      source={{ uri: sanitizedUrl }}
      style={style}
      contentFit={resizeMode === "contain" ? "contain" : "cover"}
      transition={Platform.OS === "android" ? 0 : 150}
      cachePolicy="memory-disk"
      onError={() => setHasError(true)}
    />
  );
}

export function MediaEditor({
  media,
  isSaving,
  editable,
  contacts = [],
  groups = [],
  onSave,
  onCancel,
  children,
  rightActions,
}: IMediaEditorProps) {
  const parseCoord = (val: any): number | undefined => {
    if (val == null || val === "") return undefined;
    const num = typeof val === "number" ? val : Number(val);
    return isNaN(num) ? undefined : num;
  };

  const getInitialText = (loc: any, metaLabel: any) =>
    loc && typeof loc === "object" ? loc.text || "" : typeof loc === "string" ? loc : metaLabel || "";
  const getInitialLat = (loc: any, metaLat: any, rawLat: any) =>
    loc && typeof loc === "object" ? parseCoord(loc.latitude) : parseCoord(metaLat ?? rawLat);
  const getInitialLon = (loc: any, metaLon: any, rawLon: any) =>
    loc && typeof loc === "object" ? parseCoord(loc.longitude) : parseCoord(metaLon ?? rawLon);
  const getInitialApprox = (loc: any) =>
    loc && typeof loc === "object" ? Boolean(loc.isApproximate) : false;

  const [editTitle, setEditTitle] = useState(media.title || "");
  const editName = editTitle;
  const setEditName = setEditTitle;
  const [editLocation, setEditLocation] = useState(getInitialText(media.location, media.metadata?.gpsLocationLabel));
  const [editLatitude, setEditLatitude] = useState<number | undefined>(
    getInitialLat(media.location, media.metadata?.gpsLatitude, (media as any).latitude)
  );
  const [editLongitude, setEditLongitude] = useState<number | undefined>(
    getInitialLon(media.location, media.metadata?.gpsLongitude, (media as any).longitude)
  );
  const [editIsApproximate, setEditIsApproximate] = useState<boolean>(getInitialApprox(media.location));
  const [editDescription, setEditDescription] = useState<string>(media.description || "");
  const [editFavouriteRating, setEditFavouriteRating] = useState<number>(media.favouriteRating ?? 3);
  const [titleError, setTitleError] = useState("");
  const nameError = titleError;
  const setNameError = setTitleError;
  const [mapsModalVisible, setMapsModalVisible] = useState(false);
  const [mapsAnchor, setMapsAnchor] = useState<IPopoverAnchor | null>(null);

  const rawMediaDate = media?.date;
  const initialDateParsed = React.useMemo(() => DateParser.parse(rawMediaDate), [rawMediaDate]);
  const [dateText, setDateText] = useState(
    () => initialDateParsed.formatted || (typeof rawMediaDate === "string" ? rawMediaDate : "")
  );
  const [datePayload, setDatePayload] = useState<IDate>(() => initialDateParsed);
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [dateAnchor, setDateAnchor] = useState<IPopoverAnchor | null>(null);

  const handleDateBlur = React.useCallback(() => {
    const parsed = DateParser.parse(dateText);
    setDateText(parsed.formatted);
    setDatePayload(parsed);
  }, [dateText]);

  const services = useFirebase();
  const currentUserId = services?.auth?.currentUser?.uid || "";
  const isOwner = media.userId === currentUserId;
  const tintColor = useThemeColor({}, "tint");
  const badgeBg = useThemeColor({}, "buttonSecondary");

  const { isEditingField, bindField } = useActiveFieldTracker();

  useEffect(() => {
    if (media) {
      if (!isEditingField("title") && media.title !== undefined) {
        setEditTitle(media.title || "");
      }
      if (!isEditingField("location")) {
        setEditLocation(getInitialText(media.location, media.metadata?.gpsLocationLabel));
        setEditLatitude(getInitialLat(media.location, media.metadata?.gpsLatitude, (media as any).latitude));
        setEditLongitude(getInitialLon(media.location, media.metadata?.gpsLongitude, (media as any).longitude));
        setEditIsApproximate(getInitialApprox(media.location));
      }
      if (!isEditingField("description") && media.description !== undefined) {
        setEditDescription(media.description || "");
      }
      if (!isEditingField("favouriteRating") && media.favouriteRating !== undefined) {
        setEditFavouriteRating(media.favouriteRating ?? 3);
      }
      const currentMediaDate = media.date;
      if (!isEditingField("date") && currentMediaDate !== undefined) {
        const parsed = DateParser.parse(currentMediaDate);
        setDateText(parsed.formatted);
        setDatePayload(parsed);
      }
    }
  }, [media?.id, media?.title, media?.location, media?.description, media?.favouriteRating, media?.date, isEditingField]);

  const [selectedTags, setSelectedTags] = useState<Record<string, ITagAssociationMeta>>(media?.tags || {});
  const [selectedContacts, setSelectedContacts] = useState<Record<string, IEntityContactMeta>>(media?.contacts || {});
  const [selectedGroups, setSelectedGroups] = useState<Record<string, IEntityGroupMeta>>(media?.groups || {});
  const [selectedPlaces, setSelectedPlaces] = useState<Record<string, IEntityPlaceMeta>>(media?.places || {});
  const [selectedAnchors, setSelectedAnchors] = useState<Record<string, IEntityAnchorMeta>>(media?.anchors || {});

  const parseInitialMemoriesMap = React.useCallback((): Record<string, IEntityMemoryMeta> => {
    if (media?.memories && typeof media.memories === "object" && !Array.isArray(media.memories)) {
      return media.memories;
    }
    if (Array.isArray(media?.memoryItems) && media.memoryItems.length > 0) {
      const map: Record<string, IEntityMemoryMeta> = {};
      media.memoryItems.forEach((mem, idx) => {
        if (mem.id) {
          map[mem.id] = {
            memoryId: mem.id,
            addedAt: Date.now(),
            addedBy: currentUserId,
            order: idx,
            displayName: mem.title || "Memory",
          };
        }
      });
      return map;
    }
    if (Array.isArray(media?.memoryIds) && media.memoryIds.length > 0) {
      const map: Record<string, IEntityMemoryMeta> = {};
      media.memoryIds.forEach((id: string, idx: number) => {
        map[id] = {
          memoryId: id,
          addedAt: Date.now(),
          addedBy: currentUserId,
          order: idx,
          displayName: "Memory",
        };
      });
      return map;
    }
    return {};
  }, [media?.memories, media?.memoryItems, media?.memoryIds, currentUserId]);

  const [selectedMemories, setSelectedMemories] = useState<Record<string, IEntityMemoryMeta>>(parseInitialMemoriesMap);

  const isExtractEnabled = useFeatureFlag("enable_extract_media_info", true);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedMetadata, setExtractedMetadata] = useState<any | null>(null);
  const [extractModalVisible, setExtractModalVisible] = useState(false);

  const handleExtractMediaInfo = async () => {
    if (isExtracting) return;
    setIsExtracting(true);
    try {
      const targetPayload = media;
      const res = await extractMediaMetadata(targetPayload, media.contentType || "image/jpeg");
      setExtractedMetadata(res);
      setExtractModalVisible(true);
    } catch (err) {
      console.warn("[MediaEditor] Metadata extraction failed:", err);
    } finally {
      setIsExtracting(false);
    }
  };

  const handleApplyExtractedInfo = (applied: any) => {
    if (applied.date) {
      const parsed = DateParser.parse(applied.date);
      setDateText(parsed.formatted);
      setDatePayload(parsed);
    }
    if (applied.location) {
      if (applied.location.text) setEditLocation(applied.location.text);
      if (applied.location.latitude != null) setEditLatitude(applied.location.latitude);
      if (applied.location.longitude != null) setEditLongitude(applied.location.longitude);
    }
    if (applied.tags) {
      setSelectedTags(applied.tags);
    }
    if (applied.description) {
      setEditDescription(applied.description);
    }
  };

  const memoryTitlesById = React.useMemo(() => {
    const titles: Record<string, string> = {};
    if (Array.isArray(media?.memoryItems)) {
      media.memoryItems.forEach((mem) => {
        if (mem.id) titles[mem.id] = mem.title;
      });
    }
    return titles;
  }, [media?.memoryItems]);

  const contactTitlesById = React.useMemo(() => {
    const titles: Record<string, string> = {};
    if (Array.isArray(contacts)) {
      contacts.forEach((c) => {
        const id = c.id || (c as any).contactId;
        const name = c.displayName || (c as any).fullName || (c as any).name || c.email;
        if (id && name) titles[id] = name;
      });
    }
    if (Array.isArray(media?.contactItems)) {
      media.contactItems.forEach((c) => {
        const id = c.id || (c as any).contactId;
        const name = c.displayName || (c as any).fullName || (c as any).name || c.email;
        if (id && name) titles[id] = name;
      });
    }
    return titles;
  }, [contacts, media?.contactItems]);

  const groupTitlesById = React.useMemo(() => {
    const titles: Record<string, string> = {};
    if (Array.isArray(groups)) {
      groups.forEach((g) => {
        const id = g.id || (g as any).groupId;
        const name = g.name || (g as any).displayName;
        if (id && name) titles[id] = name;
      });
    }
    if (Array.isArray(media?.groupItems)) {
      media.groupItems.forEach((g) => {
        const id = g.id || (g as any).groupId;
        const name = g.name || (g as any).displayName;
        if (id && name) titles[id] = name;
      });
    }
    return titles;
  }, [groups, media?.groupItems]);

  useEffect(() => {
    if (media?.tags) {
      setSelectedTags(media.tags);
    }
    if (media?.contacts) {
      setSelectedContacts(media.contacts);
    }
    if (media?.groups) {
      setSelectedGroups(media.groups);
    }
    if (media?.places) {
      setSelectedPlaces(media.places);
    }
    if (media?.anchors) {
      setSelectedAnchors(media.anchors);
    }
    if (!isEditingField("memories") && (media?.memories || media?.memoryItems || media?.memoryIds)) {
      setSelectedMemories(parseInitialMemoriesMap());
    }
  }, [media?.tags, media?.contacts, media?.groups, media?.places, media?.anchors, media?.memories, media?.memoryItems, media?.memoryIds, isEditingField, parseInitialMemoriesMap]);

  const handleSave = () => {
    if (!(editTitle || "").trim()) {
      setTitleError("Title is required");
      return;
    }
    const locationObj: ILocation | undefined = ((editLocation || "").trim() || editLatitude != null || editLongitude != null)
      ? {
          ...((editLocation || "").trim() ? { text: (editLocation || "").trim() } : {}),
          ...(editLatitude != null ? { latitude: editLatitude } : {}),
          ...(editLongitude != null ? { longitude: editLongitude } : {}),
          ...(editIsApproximate ? { isApproximate: true } : {}),
        }
      : undefined;

    const effectivePayload = (datePayload && datePayload.formatted)
      ? datePayload
      : (dateText || "").trim()
        ? DateParser.parse((dateText || "").trim())
        : undefined;

    const dateToSave = effectivePayload && effectivePayload.formatted
      ? JSON.stringify({
          ...effectivePayload,
          userDefined: true,
          timezoneOffset: new Date().getTimezoneOffset(),
        })
      : undefined;

    onSave(editTitle, locationObj, editDescription, editFavouriteRating, selectedTags, dateToSave, selectedContacts, selectedGroups, selectedMemories, selectedPlaces, selectedAnchors);
  };

  const locationObj: ILocation | undefined = ((editLocation || "").trim() || editLatitude != null || editLongitude != null)
    ? {
        ...((editLocation || "").trim() ? { text: (editLocation || "").trim() } : {}),
        ...(editLatitude != null ? { latitude: editLatitude } : {}),
        ...(editLongitude != null ? { longitude: editLongitude } : {}),
        ...(editIsApproximate ? { isApproximate: true } : {}),
      }
    : undefined;

  const effectiveAutoSavePayload = (datePayload && datePayload.formatted)
    ? datePayload
    : (dateText || "").trim()
      ? DateParser.parse((dateText || "").trim())
      : undefined;

  const autoSaveDate = effectiveAutoSavePayload && effectiveAutoSavePayload.formatted
    ? JSON.stringify({
        ...effectiveAutoSavePayload,
        userDefined: true,
        timezoneOffset: new Date().getTimezoneOffset(),
      })
    : undefined;

  const autoSave = useAutoSave({
    data: {
      title: editTitle,
      name: editName,
      location: locationObj,
      description: editDescription,
      favouriteRating: editFavouriteRating,
      tags: selectedTags,
      date: autoSaveDate,
      contacts: selectedContacts,
      groups: selectedGroups,
      memories: selectedMemories,
      places: selectedPlaces,
      anchors: selectedAnchors,
    },
    enabled: editable && !!(editTitle || "").trim(),
    debounceMs: 600,
    onSave: async (d) => {
      await onSave(d.title, d.location, d.description, d.favouriteRating, d.tags, d.date, d.contacts, d.groups, d.memories, d.places, d.anchors);
    },
  });

  return (
    <View style={styles.container}>
      <ThemedProgressBar loading={isExtracting} />
      <MediaPreview url={media.downloadUrl} contentType={media.contentType} name={media.title || "Media"} style={styles.fullImage} resizeMode="contain" />

      <View style={styles.formSection}>
        <ThemedView style={styles.section}>
          <View style={styles.inputGroup}>
            <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
              <ThemedText type="fieldLabel">Title</ThemedText>
              {isExtractEnabled && editable && (
                <ThemedButton
                  title={isExtracting ? "Extracting..." : "Extract Media Info"}
                  icon={isExtracting ? undefined : "sparkles-outline"}
                  variant="minimal"
                  size="small"
                  onPress={handleExtractMediaInfo}
                  disabled={isExtracting}
                  loading={isExtracting}
                  testID="media-editor-extract-btn"
                />
              )}
            </View>
            <ThemedInput
              {...bindField("title")}
              {...bindField("name")}
              value={editTitle}
              onChangeText={(text) => {
                setEditTitle(text);
                if (titleError) setTitleError("");
              }}
              style={styles.editInput}
              placeholder="Title"
              editable={editable}
              error={titleError}
            />
          </View>

          {/* Horizontal Row: Date & Location */}
          <View style={styles.horizontalRow}>
            <View style={styles.columnField}>
              <View style={styles.fieldHeaderContainer}>
                <ThemedText type="fieldLabel" style={styles.fieldHeaderLabel}>Media Date</ThemedText>
                {editable && (
                  <TouchableOpacity
                    style={[styles.mapBtn, { backgroundColor: badgeBg }]}
                    onPress={(e: any) => {
                      setDateAnchor(extractAnchorCoordinates(e));
                      setDatePickerVisible(true);
                    }}
                    accessibilityLabel="Open DatePicker to select media date"
                  >
                    <Ionicons name="calendar-outline" size={13} color={tintColor} style={{ marginRight: 4 }} />
                    <ThemedText style={[styles.mapBtnText, { color: tintColor }]}>Date</ThemedText>
                  </TouchableOpacity>
                )}
              </View>
              <ThemedInput
                {...bindField("date")}
                value={dateText}
                onChangeText={(text) => {
                  setDateText(text);
                  setDatePayload(DateParser.parse(text));
                }}
                onBlur={() => {
                  bindField("date").onBlur();
                  handleDateBlur();
                }}
                placeholder="e.g. ~Oct-26, 09-Oct, or YYYY-MM-DD"
                style={styles.editInput}
                editable={editable}
              />
            </View>
            <View style={[styles.columnField, { marginLeft: 12 }]}>
              <View style={styles.fieldHeaderContainer}>
                <ThemedText type="fieldLabel" style={styles.fieldHeaderLabel} numberOfLines={1}>Location</ThemedText>
                {editable && (
                  <TouchableOpacity
                    style={[styles.mapBtn, { backgroundColor: badgeBg }]}
                    onPress={(e: any) => {
                      setMapsAnchor(extractAnchorCoordinates(e));
                      setMapsModalVisible(true);
                    }}
                    accessibilityLabel="Open Google Maps to select location"
                  >
                    <Ionicons name="map-outline" size={13} color={tintColor} style={{ marginRight: 4 }} />
                    <ThemedText style={[styles.mapBtnText, { color: tintColor }]}>Map</ThemedText>
                  </TouchableOpacity>
                )}
              </View>
              <View style={styles.locationInputContainer}>
                <ThemedInput
                  {...bindField("location")}
                  value={editLocation}
                  onChangeText={setEditLocation}
                  placeholder="Location"
                  style={styles.editInput}
                  editable={editable}
                />
                {(typeof editLatitude === "number" && !isNaN(editLatitude) && typeof editLongitude === "number" && !isNaN(editLongitude) || editIsApproximate) && (
                  <View style={styles.coordOverlayContainer}>
                    {typeof editLatitude === "number" && !isNaN(editLatitude) && typeof editLongitude === "number" && !isNaN(editLongitude) && (
                      <View style={[styles.coordBadge, { backgroundColor: badgeBg }]}>
                        <Ionicons name="navigate-outline" size={11} color={tintColor} style={{ marginRight: 4 }} />
                        <ThemedText style={styles.coordBadgeText}>
                          {editLatitude.toFixed(4)}, {editLongitude.toFixed(4)}
                        </ThemedText>
                        {editable && (
                          <TouchableOpacity
                            onPress={() => {
                              setEditLatitude(undefined);
                              setEditLongitude(undefined);
                            }}
                            style={styles.clearCoordBtn}
                            accessibilityLabel="Clear coordinates"
                          >
                            <Ionicons name="close-circle" size={14} color="#888" />
                          </TouchableOpacity>
                        )}
                      </View>
                    )}
                    {editIsApproximate && (
                      <View style={[styles.coordBadge, { backgroundColor: badgeBg }]}>
                        <Ionicons name="help-circle-outline" size={11} color={tintColor} style={{ marginRight: 4 }} />
                        <ThemedText style={styles.coordBadgeText}>Approximate</ThemedText>
                        {editable && (
                          <TouchableOpacity
                            onPress={() => setEditIsApproximate(false)}
                            style={styles.clearCoordBtn}
                            accessibilityLabel="Remove approximate flag"
                          >
                            <Ionicons name="close-circle" size={14} color="#888" />
                          </TouchableOpacity>
                        )}
                      </View>
                    )}
                  </View>
                )}
              </View>
            </View>
          </View>

          <View style={styles.inputGroup}>
            <ThemedText type="fieldLabel">Description</ThemedText>
            <CollaborativeEditor
              {...bindField("description")}
              entityId={media?.id}
              entityType="media"
              initialContent={editDescription}
              onChange={setEditDescription}
              placeholder="Description"
              editable={editable}
              contacts={contacts && contacts.length > 0 ? contacts : Object.values(selectedContacts).map((c) => ({ id: c.contactId, displayName: contactTitlesById[c.contactId] || c.displayName || "Contact" }))}
              groups={groups && groups.length > 0 ? groups : Object.values(selectedGroups).map((g) => ({ id: g.groupId, name: groupTitlesById[g.groupId] || g.displayName || "Group" }))}
              memories={Object.values(selectedMemories).map((m) => ({ id: m.memoryId, title: memoryTitlesById[m.memoryId] || m.displayName || "Memory" }))}
            />
          </View>

          <View style={[styles.inputGroup, styles.ratingRow]}>
            <ThemedText type="fieldLabel">Favourite Rating</ThemedText>
            <ThemedRating
              value={editFavouriteRating}
              onChange={setEditFavouriteRating}
              readOnly={!editable}
              size={18}
              showLabel={false}
              style={styles.ratingControl}
            />
          </View>

          {media.metadata?.href ? (
            <View style={styles.inputGroup}>
              <ThemedText type="fieldLabel">Original Source (HRef)</ThemedText>
              <View style={[styles.readOnlyContainer, { backgroundColor: badgeBg }]}>
                <Ionicons name="link-outline" size={14} color={tintColor} style={{ marginRight: 6 }} />
                <ThemedText style={styles.readOnlyText} numberOfLines={2} selectable>
                  {media.metadata.href}
                </ThemedText>
              </View>
            </View>
          ) : null}
        </ThemedView>

        {/* Linked Contacts Section Panel */}
        <ThemedView style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Linked Contacts</ThemedText>
          <EntityLinkStrip
            entityId={media?.id}
            entityType="media"
            targetType="contact"
            links={selectedContacts}
            onLinksChange={setSelectedContacts}
            canEdit={editable}
            isOwner={isOwner}
            currentUserId={currentUserId}
            titlesById={contactTitlesById}
          />
        </ThemedView>

        {/* Linked Groups Section Panel */}
        <ThemedView style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Linked Groups</ThemedText>
          <EntityLinkStrip
            entityId={media?.id}
            entityType="media"
            targetType="group"
            links={selectedGroups}
            onLinksChange={setSelectedGroups}
            canEdit={editable}
            isOwner={isOwner}
            currentUserId={currentUserId}
            titlesById={groupTitlesById}
          />
        </ThemedView>

        {/* Linked Memories Section Panel */}
        <ThemedView style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Linked Memories</ThemedText>
          <EntityLinkStrip
            entityId={media?.id}
            entityType="media"
            targetType="memory"
            links={selectedMemories}
            onLinksChange={setSelectedMemories}
            canEdit={editable}
            isOwner={isOwner}
            currentUserId={currentUserId}
            titlesById={memoryTitlesById}
          />
        </ThemedView>

        {/* Linked Places Section Panel */}
        <ThemedView style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Linked Places</ThemedText>
          <EntityLinkStrip
            entityId={media?.id}
            entityType="media"
            targetType="place"
            links={selectedPlaces}
            onLinksChange={setSelectedPlaces}
            canEdit={editable}
            isOwner={isOwner}
            currentUserId={currentUserId}
          />
        </ThemedView>

        {/* Linked Anchors Section Panel */}
        <ThemedView style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Linked Anchors</ThemedText>
          <EntityLinkStrip
            entityId={media?.id}
            entityType="media"
            targetType="anchor"
            links={selectedAnchors}
            onLinksChange={setSelectedAnchors}
            canEdit={editable}
            isOwner={isOwner}
            currentUserId={currentUserId}
          />
        </ThemedView>

        {/* Tags Section Panel */}
        <ThemedView style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Tags</ThemedText>
          <TagListStrip
            entityId={media?.id}
            entityType="media"
            tags={selectedTags}
            onTagsChange={setSelectedTags}
            canEdit={editable}
            isOwner={isOwner}
            currentUserId={currentUserId}
          />
        </ThemedView>

        {children}

        <View style={styles.footer}>
          <View style={styles.leftFooterControls}>
            <ThemedButton title="Back" variant="minimal" onPress={onCancel} />
            {editable && <AutoSaveIndicator status={autoSave.status} errorMessage={autoSave.error} />}
          </View>
          {rightActions && (
            <View style={styles.rightFooterControls}>
              {rightActions}
            </View>
          )}
        </View>
      </View>

      {/* Google Maps Location Hover Popover */}
      <GoogleMapsLocationSelector
        visible={mapsModalVisible}
        onClose={() => setMapsModalVisible(false)}
        initialLocation={editLocation}
        initialLatitude={editLatitude}
        initialLongitude={editLongitude}
        initialIsApproximate={editIsApproximate}
        anchorPosition={mapsAnchor}
        onSelect={(result) => {
          setEditLocation(result.location);
          setEditLatitude(result.latitude);
          setEditLongitude(result.longitude);
          setEditIsApproximate(Boolean(result.isApproximate));
        }}
      />

      {/* DateSelector Hover Popover with Range selection for Media Date */}
      <DateSelector
        visible={datePickerVisible}
        onClose={() => setDatePickerVisible(false)}
        value={datePayload}
        type="Range"
        anchorPosition={dateAnchor}
        onApply={(payload) => {
          setDateText(payload.formatted);
          setDatePayload(payload);
          setDatePickerVisible(false);
        }}
      />

      <ExtractMediaInfoModal
        visible={extractModalVisible}
        media={media}
        extractedMetadata={extractedMetadata}
        onApply={handleApplyExtractedInfo}
        onClose={() => setExtractModalVisible(false)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
  fullImage: {
    width: "100%",
    height: 300,
    borderRadius: 0,
    backgroundColor: "#000",
    marginBottom: 12,
  },
  formSection: {
    gap: 12,
  },
  section: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 0,
    backgroundColor: "rgba(150, 150, 150, 0.05)",
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.05)",
    gap: 8,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 4,
  },
  horizontalRow: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  columnField: {
    flex: 1,
    minWidth: 0,
  },
  horizontalLabelsRow: {
    flexDirection: "row",
    alignItems: "flex-end",
    marginBottom: 2,
  },
  fieldHeaderContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: 18,
    marginBottom: 2,
  },
  fieldHeaderLabel: {
    marginBottom: 0,
  },
  horizontalInputsRow: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  inputGroup: {
    gap: 0,
  },
  ratingRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "nowrap",
    gap: 12,
  },
  ratingLabel: {
    marginBottom: 0,
  },
  ratingControl: {
    marginVertical: 0,
  },
  locationHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: 20,
    marginBottom: 2,
  },
  mapBtn: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 0,
  },
  mapBtnText: {
    fontSize: 11,
    lineHeight: 14,
    fontWeight: "700",
  },
  locationInputContainer: {
    position: "relative",
    zIndex: 10,
  },
  coordOverlayContainer: {
    position: "absolute",
    top: "100%",
    left: 0,
    marginTop: -8,
    zIndex: 20,
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    pointerEvents: "box-none",
  },
  coordBadge: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.08)",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.12,
    shadowRadius: 2,
    elevation: 3,
    maxWidth: "100%",
  },
  coordBadgeText: {
    fontSize: 10,
    fontWeight: "600",
    opacity: 0.85,
  },
  clearCoordBtn: {
    marginLeft: 4,
    padding: 2,
  },
  editInput: {
    marginBottom: 8,
  },
  textArea: {
    height: 100,
  },
  footer: {
    marginTop: 20,
    paddingBottom: 24,
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  leftFooterControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  rightFooterControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  saveContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  actionBtn: {
    minWidth: 140,
  },
  mediaPlaceholder: {
    justifyContent: "center",
    alignItems: "center",
    gap: 8,
  },
  mediaPlaceholderText: {
    fontSize: 12,
    fontWeight: "bold",
    opacity: 0.6,
  },
  mediaPlayerContainer: {
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(74, 144, 226, 0.05)",
    padding: 16,
    borderRadius: 0,
  },
  readOnlyContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 0,
    borderWidth: 1,
    borderColor: "rgba(150, 150, 150, 0.2)",
  },
  readOnlyText: {
    fontSize: 13,
    opacity: 0.85,
    flexShrink: 1,
  },
});
